import { useEffect, useMemo, useState } from "react";
import { Heading, CardLine } from "../../pages/IndividualView";
import theme from "../../theme";
import { formatDate } from "../../utils/formatters";
import { Box } from "../Box";
import { Flex } from "../Flex";
import { Slider } from "../Slider";
import { H3 } from "../Heading";
import { Formik } from "formik";
import { Button } from "../Button";
import { Label } from "../Label";
import { Paragraph } from "../Paragraph";
import Select from "../Select";
import { useCountries } from "../../data/useCountries";
import { CheckAddress } from "../../types/CheckAddress";
import { addressFormValidationSchema } from "../../utils/validationSchema";
import AddressAutocomplete from "../AddressAutocomplete";
import useAddressLongForm from "../../utils/forms/useAddressLongForm";
import { useGetAddressTypeList } from "../../data/useGetAddressTypeList";
import { showError } from "../../utils/error-handling";
import { notify } from "../../utils/notify";
import { useEditProfileAddress } from "../../data/useEditProfileAddress";

type Scope = "all" | "individual" | "entity";


export const AddressSection = ({ profile }: { profile: any }) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const { data } = useCountries();
  const [selectedCountry, setSelectedCountry] = useState<Option | null>(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [hasAddressSelection, setHasAddressSelection] = useState(false);
  const [showNoCoverageBox, setShowNoCoverageBox] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const { AddressLongForm } = useAddressLongForm();
  const scope: Scope = profile?._type === "individual" ? "individual" : "entity";
  const { data: addressTypes } = useGetAddressTypeList(scope, true);
  const [selectedAddressType, setSelectedAddressType] = useState(null);
  const { mutate: editProfile } = useEditProfileAddress();

  type Option = {
    label: string;
    value: string | number;
    sac: boolean;
  };

  const addressTypeOptions = useMemo(
    () =>
      addressTypes?.map(({ name, id }) => ({
        label: name,
        value: id,
      })) ?? [],
    [addressTypes],
  );
  const countryOptions = useMemo(
    () =>
      (data?.data.map(({ isoalpha2, name, sac }) => ({
        label: name,
        value: isoalpha2,
        sac: sac,
      })) as Option[]) ?? [],
    [data],
  );

  useEffect(() => {
    if (!isEditOpen || !selectedAddress) return;

    const country =
      countryOptions?.find((c) => c.value === selectedAddress?.country_code) ??
      null;
    const addressTypeId = selectedAddress?._type?.id;
    const addressType =
      addressTypeId != null
        ? addressTypeOptions?.find((c) => c.value === addressTypeId) ?? null
        : null;

    if (country) {
      setSelectedCountry(country);
    }
    if (addressType) {
      setSelectedAddressType(addressType);
    }
    setShowNoCoverageBox(country ? !country.sac : false);
    setFormOpen(true);
    setHasAddressSelection(true);
  }, [
    isEditOpen,
    selectedAddress,
    countryOptions,
    addressTypeOptions,
  ]);


  const getTypeLabel = (a: any) => {
    const t = (a?._type?.name || "").toLowerCase();
    if (t === "registered") return "Registered";
    if (t === "correspondence") return "Correspondence";
    if (t === "branch") return "Branch";
    return a?._type?.name || "Address";
  };

  const renderLine1 = (a: any) =>
    [
      a?.flat_appartment_subbuilding,
      a?.building_number,
      a?.building_name,
      a?.road_street,
    ]
      .filter(Boolean)
      .join(" ");

  const renderLine2 = (a: any) =>
    [a?.town_city, a?.district].filter(Boolean).join(", ");

  const initialValues: CheckAddress = {
    building_number: "",
    flat_appartment_subbuilding: "",
    building_name: "",
    road_street: "",
    town_city: "",
    district: "",
    state_province_name: "",
    state_province_code: "",
    post_zip_code: "",
    country_code: "",
    _type: null,
  };

  const toId = (value: any) =>
    typeof value === "object" && value !== null ? value.id ?? null : value ?? null;

  const clearAddressFields = (formik: any, countryCode: string) => {
    const emptyAddress = {
      building_number: "",
      flat_appartment_subbuilding: "",
      building_name: "",
      road_street: "",
      town_city: "",
      district: "",
      state_province_name: "",
      state_province_code: "",
      post_zip_code: "",
      country_code: countryCode,
      country: countryCode,
    };

    Object.entries(emptyAddress).forEach(([key, value]) => {
      formik.setFieldValue(key, value);
    });
  };

  const handleNext = (values) => {
    setIsEditOpen(false);
    setSelectedAddress(null);
    //setSelectedAddressType(null);
    setSelectedCountry(null);
    const profileId = profile.id;
    const existingAddresses = profile.address || [];

    const addressExists = existingAddresses.some((addr) => addr.id === values.id);
    let updatedAddress;

    if (addressExists) {
      //edit flow
      updatedAddress = existingAddresses.map((addr) =>
        addr.id === values.id
          ? {
            ...addr,
            ...values,
            country_code: selectedCountry?.value?.toString() ?? values.country_code,
          }
          : addr
      );
    } else {
      updatedAddress = [
        ...existingAddresses,
        {
          ...values,
          country_code: selectedCountry?.value?.toString() ?? values.country_code,
        },
      ];
    }

    const normalizedAddresses = updatedAddress.map((addr) => {
      const {
        profile,
        _type,
        created_by,
        ...rest
      } = addr;
      return {
        ...rest,
        _type: typeof addr._type === "object"
          ? addr._type?.id
          : addr._type,
      }

    });

    const payload = {
      ...profile,
      address: normalizedAddresses,
      country: toId(profile?.country),
      entity_type: toId(profile?.entity_type),
      company_status: toId(profile?.company_status),
      owner: toId(profile?.owner),
    }

    editProfile({ profileId, ...payload } as any, {
      onSuccess: () => {
        notify("Profile address details updated successfully!", {
          position: "bottom-left",
          type: "success",
        });
      },
      onError: (err: any) => {
        showError(err, "Failed to update Profile address details.");
      },
    });

  }
  return (
    <Box
      mt={3}
      background={theme.colors.white}
      borderRadius={12}
      p={16}
      border={`1px solid ${theme.colors.divider}`}
    >
      <Flex justifyContent="space-between" alignItems="center" mb={2}>
        <Heading fontSize={14}>Address</Heading>

        <Flex gap="12" alignItems="center">
          {profile?.address?.[0]?.updated_on && (
            <CardLine color={theme.colors.muted}>
              Last updated {formatDate(profile?.address[0].updated_on)}
            </CardLine>
          )}

          <span
            style={{ color: theme.colors.link, cursor: "pointer", fontWeight: 600 }}
            onClick={() => {
              setIsEditOpen(true);
              setSelectedAddress(null);
              setSelectedCountry(null);
              setSelectedAddressType(null);
              setHasAddressSelection(false);
              setFormOpen(false);
            }}
          >
            Add
          </span>
        </Flex>
      </Flex>

      {profile?.address.length === 0 ? (
        <CardLine>No address found.</CardLine>
      ) : (
        <Box>
          {profile?.address.map((a, i) => (
            <Flex key={i} gap="24" p="10px 0">
              <Box flex={0.9}>
                <Heading fontSize={12} color={theme.colors.muted}>
                  {getTypeLabel(a)}
                </Heading>
              </Box>

              <Box flex={1.6}>
                <CardLine>{renderLine1(a) || "-"}</CardLine>
                <CardLine>{renderLine2(a) || "-"}</CardLine>
                <CardLine>{a?.post_zip_code ?? "-"}</CardLine>
              </Box>

              <Box flex={1}>
                <Flex gap="8" alignItems="center">
                  {a?.country_flag ? (
                    <Box
                      backgroundColor={"gray.10"}
                      borderRadius={"50%"}
                      p={"1"}
                    >
                      <img
                        src={`data:image/png;base64,${a.country_flag}`}
                        alt="flag"
                        style={{ display: "block" }}
                      />
                    </Box>
                  ) : null}
                  <CardLine>{a?.country_name ?? "-"}</CardLine>
                </Flex>
              </Box>

              <Box flex={1.2}>
                <CardLine>
                  {a?.date_from ? (
                    <>
                      From {formatDate(a.date_from)}
                      {a?.date_to ? <> to {formatDate(a.date_to)}</> : null}
                    </>
                  ) : (
                    "-"
                  )}
                </CardLine>
              </Box>
              <span
                style={{ color: theme.colors.link, cursor: "pointer", fontWeight: 600 }}
                onClick={() => {
                  setSelectedAddress(a);
                  const country = countryOptions?.find((c) => c.value === a?.country_code);
                  const adressType = addressTypeOptions?.find((c) => c.value === a?._type?.id);
                  setSelectedCountry(country || null);
                  setSelectedAddressType(adressType || null);
                  setHasAddressSelection(true);
                  setFormOpen(true);
                  setIsEditOpen(true);
                }}
              >
                Edit
              </span>
            </Flex>
          ))}
        </Box>
      )}
      <Slider
        isOpen={isEditOpen}
        onRequestClose={() => setIsEditOpen(false)}
        width="600px"
      >
        <Box p={3} overflowY="auto" height={"calc(100vh - 211px)"}>
          <Flex justifyContent="space-between" alignItems="center" mb={3}>
            <H3 fontSize={16}>
              Edit Address Details
            </H3>
          </Flex>
          <Formik
            enableReinitialize
            initialValues={selectedAddress
              ? {
                building_number: selectedAddress?.building_number || "",
                flat_appartment_subbuilding:
                  selectedAddress?.flat_appartment_subbuilding || "",
                building_name: selectedAddress?.building_name || "",
                road_street: selectedAddress?.road_street || "",
                town_city: selectedAddress?.town_city || "",
                district: selectedAddress?.district || "",
                state_province_name:
                  selectedAddress?.state_province_name || "",
                state_province_code:
                  selectedAddress?.state_province_code || "",
                post_zip_code: selectedAddress?.post_zip_code || "",
                country_code: selectedAddress?.country_code || "",
                _type: selectedAddress?._type?.id || null,
                country: selectedAddress?.country_code,
                id: selectedAddress?.id,
              }
              : initialValues
            }
            validationSchema={() =>
              addressFormValidationSchema(selectedCountry?.value.toString())
            }
            onSubmit={handleNext}
          >
            {(formik) => (

              <>
                <Box position={"relative"}>
                  <Select
                    value={selectedAddressType}
                    options={addressTypeOptions}
                    onChange={(option: Option) => {
                      setSelectedAddressType(option);
                      formik.setFieldValue("_type", option?.value);
                    }}
                    flex={1}
                    label="Address Type"
                    name="_type"
                    isRequired
                    hasError={Boolean(!selectedAddressType)}
                    errorMessage={selectedAddressType ? "" : "Address Type is required"}
                  />

                  <Select mt={2}
                    value={selectedCountry}
                    options={countryOptions}
                    onChange={(option: Option) => {
                      const nextCountryCode = option?.value?.toString() || "";
                      const currentCountryCode =
                        formik.values.country_code?.toString() || "";
                      const countryChanged =
                        nextCountryCode !== currentCountryCode;

                      setSelectedCountry(option);
                      formik.setFieldValue("country_code", nextCountryCode);
                      formik.setFieldValue("country", nextCountryCode);

                      if (countryChanged) {
                        setHasAddressSelection(false);
                        setFormOpen(false);
                        clearAddressFields(formik, nextCountryCode);
                      }

                      if (!option.sac) {
                        setShowNoCoverageBox(true);
                      } else {
                        setShowNoCoverageBox(false);
                      }
                    }}
                    flex={1}
                    label="Country"
                    name="country"
                    isRequired
                    hasError={Boolean(!selectedCountry)}
                    errorMessage={selectedCountry ? "" : "Country is required"}
                  />

                  {showNoCoverageBox && (
                    <Box
                      width={250}
                      position={"fixed"}
                      top={250}
                      right={30}
                      backgroundColor={"gray.100"}
                      p={1}
                      borderRadius={12}
                    >
                      <H3 fontSize={1}>No address coverage</H3>
                      <Paragraph fontSize={0}>
                        Our address verification service does not currently cover the
                        country you have selected.
                      </Paragraph>
                    </Box>
                  )}
                </Box>

                {/* !showNoCoverageBox && */}


                {selectedCountry && (
                  <form onSubmit={formik.handleSubmit}>
                    <Box mt={2}>
                      <Flex
                        alignItems={"center"}
                        justifyContent={"space-between"}
                      >
                        <Label>Address</Label>
                        <Button
                          variant="text"
                          type="button"
                          onClick={() => setFormOpen(true)}
                        >
                          Enter address manually
                        </Button>
                      </Flex>
                      <AddressAutocomplete
                        setSelectedAddress={(address) => {
                          setHasAddressSelection(true);
                          setFormOpen(true);
                          Object.keys(address).forEach((key) => {
                            formik.setFieldValue(key, address[key]);
                          });
                        }}
                        country={selectedCountry.value}
                        setFormOpen={setFormOpen}
                      />
                    </Box>

                    {formOpen && <AddressLongForm formik={formik} />}

                    <Flex justifyContent="flex-end" my={3}>
                      <Button
                        type="submit"
                        isDisabled={
                          !selectedCountry ||
                          !formik.values.country_code ||
                          (!hasAddressSelection && !(formik.isValid && formik.dirty))
                        }
                      >
                        Save
                      </Button>
                    </Flex>
                  </form>
                )}

              </>
            )}
          </Formik>
        </Box>
      </Slider >
    </Box >

  );
};
